import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Container,
  Paper,
  Grid,
  CircularProgress,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import styled from "styled-components";
import html2canvas from "html2canvas";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as pdfjsLib from "pdfjs-dist";
import { GlobalWorkerOptions } from "pdfjs-dist";

const ScrollableBox = styled(Box)`
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  overflow-y: auto;
  max-height: 8em;
  font-size: 0.6em;

  @media (max-width: 600px) {
    max-height: 5em;
  }
`;

const PDFViewer = styled(Box)`
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  max-height: 400px;
  overflow-y: auto;

  @media (max-width: 600px) {
    max-height: 300px;
  }
`;

const SignatureCanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 150px;

  .sigCanvas {
    width: 100% !important;
    height: 100% !important;
    background-color: #ccc;
  }
`;

const SendInvoiceForm = () => {
  const [receiverName, setReceiverName] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [pdfContent, setPdfContent] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(false);
  const sigCanvas = useRef(null);
  const formRef = useRef(null);

  // Configure PDF.js worker
  useEffect(() => {
    const workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.3.122/pdf.worker.min.js`;
    GlobalWorkerOptions.workerSrc = workerSrc;
  }, []);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (formRef.current) {
      html2canvas(formRef.current).then(async (canvas) => {
        const screenshotDataUrl = canvas.toDataURL();
        const screenshotBlob = await fetch(screenshotDataUrl).then((res) =>
          res.blob()
        );

        const formData = new FormData();
        formData.append("receiverName", receiverName);
        if (receiptFile) formData.append("receiptFile", receiptFile);
        if (signature) {
          const signatureBlob = await (await fetch(signature)).blob();
          formData.append("signature", signatureBlob, "signature.png");
        }
        formData.append("screenshot", screenshotBlob, "screenshot.png");

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/v1/receipts`,
            {
              method: "POST",
              body: formData,
            }
          );

          if (response.ok) {
            toast.success("Submitted successfully");
            setReceiverName("");
            setReceiptFile(null);
            setSignature(null);
            setPdfContent([]);
            setTermsAccepted(false);
            sigCanvas.current.clear();
          } else {
            toast.error("Submission failed");
          }
        } catch (error) {
          toast.error("Error submitting");
        } finally {
          setLoading(false);
        }
      });
    }
  };

  useEffect(() => {
    if (receiptFile) {
      const fileReader = new FileReader();
      fileReader.onload = async (e) => {
        const pdfData = new Uint8Array(e.target.result);
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        const numPages = pdf.numPages;
        const pages = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement("canvas");
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const canvasContext = canvas.getContext("2d");
          await page.render({ canvasContext, viewport }).promise;
          pages.push(canvas.toDataURL());
        }
        setPdfContent(pages);
      };
      fileReader.readAsArrayBuffer(receiptFile);
    }
  }, [receiptFile]);

  return (
    <Container
      component="main"
      maxWidth="xl"
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          padding: { xs: 2, md: 4 },
          height: "100%",
          overflow: "auto",
        }}
      >
        <form onSubmit={handleFormSubmit} ref={formRef}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Receiver Name"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <label htmlFor="upload-invoice">
                Upload Invoice (PDF)
                <input
                  type="file"
                  id="upload-invoice"
                  accept="application/pdf"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  required
                  style={{ display: "block", marginTop: "10px" }}
                />
              </label>
            </Grid>
            <Grid item xs={12}>
              {pdfContent.length > 0 && (
                <PDFViewer>
                  {pdfContent.map((pageDataUrl, index) => (
                    <img
                      key={index}
                      src={pageDataUrl}
                      alt={`PDF Page ${index + 1}`}
                      style={{ width: "100%" }}
                    />
                  ))}
                </PDFViewer>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Sign Here:
              </Typography>
              <SignatureCanvasWrapper>
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  clearOnResize={false}
                  canvasProps={{
                    className: "sigCanvas",
                  }}
                  onEnd={() => setSignature(sigCanvas.current.toDataURL())}
                />
              </SignatureCanvasWrapper>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => {
                  sigCanvas.current.clear();
                  setSignature(null);
                }}
                sx={{ backgroundColor: "gray", color: "white", marginTop: 2 }}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12}>
              <ScrollableBox>
                <Typography variant="body2">
                  Please read and accept the terms and conditions to proceed.
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                ALL SALES OF ARTEN SOLUTIONS, LLC. PRODUCTS AND SERVICES,
                  HEREIN REFERRED TO AS THE SELLER, ARE SUBJECT TO EACH OF THE
                  FOLLOWING TERMS AND CONDITIONS BY THE PURCHASER NAMED ON THE
                  INCLUDED PRICE QUOTATION. FORMATION OF CONTRACT: The included
                  Quotation is an offer to sell the Purchaser the products and
                  services described in the Quotation. By signing and returning
                  the Quotation to the Seller, sending the Seller a Purchase
                  Order referencing the Quotation or using a credit card to make
                  payment, the Purchaser agrees to purchase the products and
                  services on the terms and conditions specified in the
                  Quotation and these Terms and Conditions. CANCELLATION: Orders
                  received by the seller are accepted with the understanding
                  that they are not subject to cancellation. PRICES: Prices for
                  products, parts and services are stated on the included price
                  quotation. DELIVERY: Shipping and delivery dates are
                  approximate and not guaranteed unless otherwise specified on
                  the face hereof, Partial shipments of complete orders will be
                  made by Seller when ready and invoiced absent an agreement in
                  writing to the contrary. Delay in delivery of any installments
                  shall not relieve the Purchaser of : a) its obligation to pay
                  for any installments received prior to such delay and, b) its
                  obligation to accept the remaining deliveries. If because of
                  acts of God, strikes, lockouts or other industrial
                  disturbances, wars, blockages, insurrections, riots, arrests,
                  explosions, sabotage, fires, floods, perils of the sea,
                  accidents to machinery, delays in transportation, inability of
                  Seller to obtain the whole or any part of merchandise
                  contracted for by Seller, precedence or priorities granted at
                  the request, demand or for the benefit, directly or
                  indirectly, of Federal or State Government or any subdivision
                  or agency thereof, or other causes not within the control of
                  Seller. Seller shall be unable to carry out its obligations
                  under this contract either wholly or in part, such obligations
                  shall be suspended during the continuance of such hindrances
                  and the obligations, terms and conditions of this contract
                  shall be extended for such periods as may be necessary for the
                  purpose of making good any suspension of deliveries so caused
                  Seller shall not in any event be liable for failure to deliver
                  or perform or for delays in delivery or performance occasioned
                  by such causes. ROUTING: In the absence of Purchaser’s
                  instructions as to desired carrier and routing of goods, goods
                  will be forwarded and routed as Seller may determine. DAMAGE
                  IN TRANSIT: Delivery of the finished goods hereunder to
                  carrier shall constitute delivery to Purchaser and all risk of
                  loss or damage after delivery to carrier shall be borne solely
                  by Purchaser. All claims for loss or damage in transit should
                  be made to the carrier. DAMAGE IN USE OR STORAGE: Seller is
                  not responsible for loss or damage to goods because of
                  improper installation or use, or if improperly handled,
                  maintained, stored, or serviced. CLAIMS OR RETURNS: All claims
                  must be made in writing and delivered to Seller within 10 days
                  after receipt of goods and must be accompanied by Seller’s
                  packing list and freight bill. Failure to make such claims
                  within 10 days will constitute a waiver by the Buyer of such
                  claims. COMPLIANCE WITH STATUTES: Seller agrees to comply with
                  requirements of all applicable Federal, State and Local
                  statutes and laws with respect to its operations. ELECTRONIC
                  DATA: All electronic part data sent to the Seller is
                  determined to be the correct and final data at the time the
                  quote is issued. The Seller takes no responsibility for
                  incorrect, inaccurate or incomplete parts because of incorrect
                  electronic data. Electronic data should be output at the
                  desired tolerance, resolution and specifications by the
                  Purchaser when sent to the SELLER for a quotation. Additional
                  parts built with updated or different data will be quoted at
                  the standard SELLER prototype service rates. TAXES: If any
                  taxes shall be levied by the Federal or any State or Local
                  Government upon goods covered hereby or in respect to the
                  production, manufacture, or sale thereof or upon
                  transportation including freight charges thereon, the amount
                  of such taxes shall be added to the purchase price specified
                  herein and should be borne by Purchaser. PAYMENT: In the event
                  the Purchaser default in any payment due Seller hereunder,
                  Seller shall be entitled to interest on all such unpaid
                  amounts at a rate equal to the lesser of: a) 18% per month or,
                  b) the maximum rate permitted by law. In addition, Purchaser
                  shall pay Seller all costs of collection on overdue accounts
                  including but not limited to reasonable attorney’s fees
                  whether or not litigation should be commenced in aid thereof.
                  Remittance must be made in U.S. funds. WAIVERS: A waiver of
                  any of the terms hereof in a single instance shall not
                  constitute a waiver in other instances nor shall a specific
                  waiver with respect to any term constitute a waiver of any
                  other term. A waiver to be binding on Seller must be in
                  writing and signed by an officer of the Seller. WARRANTY: All
                  goods sold hereunder are warranted to be free from defects in
                  materials and workmanship for a period of one month from the
                  date of shipment. Any modifications to goods sold by Purchaser
                  shall fully nullify seller warranty. THE FOREGOING WARRANTY IS
                  IN LIEU OF ALL OTHER WARRANTIES EXPRESS OR IMPLIED INCLUDING
                  BUT NOT LIMITED TO THE IMPLIED WARRANTIES OR MERCHANTABILITY
                  AND OF FITNESS FOR A PARTICULAR PURPOSE. Purchaser’s sole and
                  exclusive remedy against Seller for breach of any warranty
                  hereunder is expressly limited to repair or replacement of
                  defective material at Seller’s option but in no event shall
                  Seller’s obligation exceed the total purchase price paid for
                  the goods; Seller shall not be liable in any respect for a)
                  any loss, personal injury, property damage or costs of repairs
                  except personal injury or property damages caused solely by
                  Seller’s negligence, b) any special incidental or
                  consequential damages of any kind even if Seller has been
                  advised of the possibility of such damages, including but not
                  limited to loss of revenue, loss of profits, failure to
                  realize savings, or any liability of Purchaser to a third
                  party, c) any damages (regardless of their nature) caused
                  wholly or in part by the negligence of Purchaser or a third
                  party or, d) any delay or failure to perform Seller’s
                  obligations hereunder due to any cause beyond its reasonable
                  control. DAMAGE CLAIMS BY PURCHASER: All claims by Purchaser
                  of damages regardless of their nature,shall be deemed to have
                  been waived unless made in writing within one month of
                  Purchaser’s receipt of goods specified herein. In the event
                  there are installment deliveries, Purchaser’s claims must be
                  made in writing within one month of Purchaser’s receipt of the
                  particular installment to which the claim is applied.
                  GOVERNING LAW: This contract shall be deemed for all purposes
                  to have been made in Florida and shall be governed by and
                  construed in accordance with the laws of the state of Florida.
                  Florida courts shall have exclusive jurisdiction over all
                  controversies arising hereunder. ENTIRE AGREEMENT: Only the
                  terms and conditions contained herein constitute the entire
                  agreement between the parties and will govern transactions
                  between Buyer and Seller. No other terms and conditions that
                  Purchaser may provide or offer are accepted.
                </Typography>
              </ScrollableBox>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                  />
                }
                label="I Accept Terms and Conditions"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#9e9e9e",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#616161",
                  },
                  fontSize: "16px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  boxShadow: "0 3px 5px 2px rgba(0, 0, 0, 0.2)",
                }}
                fullWidth
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </form>
        <ToastContainer />
      </Paper>
    </Container>
  );
};

export default SendInvoiceForm;
